import { useState, useEffect } from "react";
import {
  fetchData,
  fetchSingleWaterSensor,
  fetchWsZoneAssociation
} from "../../APICalls/GetRequests";
import {
  changeWaterSensorSensitivity,
  updateWsZoneBinding
} from "../../APICalls/PutRequests";
import Popup from "../../UI/Popup";
import Cookies from "js-cookie";
import "./WaterSensorPopup.scss";

const WaterSensorPopup = ({
  isVisible,
  onClose,
  sensorId,
  fieldId,
  onSensorUpdated
}) => {
  const [waterSensor, setWaterSensor] = useState(null);
  const [zones, setZones] = useState([]);

  const [sensorName, setSensorName] = useState("");
  const [sensitivity, setSensitivity] = useState("");
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [associatedZoneId, setAssociatedZoneId] = useState(null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingSensitivity, setIsEditingSensitivity] = useState(false);
  const [isEditingZone, setIsEditingZone] = useState(false);

  const isRealName = (name) =>
    name && name.trim() !== "" && name !== "unnamed water sensor";

  useEffect(() => {
    if (!isVisible || !sensorId) return;

    const sessionKey = Cookies.get("session_key");

    const loadData = async () => {
      try {
        const [zonesData, sensorData, bindingData] = await Promise.all([
          fetchData("allZones", sessionKey, fieldId),
          fetchSingleWaterSensor(sessionKey, sensorId),
          fetchWsZoneAssociation(sessionKey, sensorId),
        ]);

        setZones(zonesData);
        setWaterSensor(sensorData);

        setSensorName(isRealName(sensorData.name) ? sensorData.name : "");
        setSensitivity(sensorData.sensPct);

        const boundZoneId = bindingData?.zone_ids?.[0] ?? null;
        setSelectedZoneId(boundZoneId);
        setAssociatedZoneId(boundZoneId);

        setIsEditingName(false);
        setIsEditingSensitivity(false);
        setIsEditingZone(false);
      } catch (err) {
        console.error("Failed to load water sensor popup data:", err);
        setZones([]);
      }
    };

    loadData();
  }, [isVisible, sensorId, fieldId]);

  const updateName = async () => {
    if (!waterSensor) return;
    const trimmedName = sensorName.trim();
    if (trimmedName === waterSensor.name) return;

    await changeWaterSensorSensitivity(
      Cookies.get("session_key"),
      { name: trimmedName, sensPct: waterSensor.sensPct },
      sensorId
    );

    setWaterSensor({ ...waterSensor, name: trimmedName });
    onSensorUpdated?.();
  };

  const updateSensitivity = async () => {
    if (!waterSensor || Number(sensitivity) === waterSensor.sensPct) return;

    await changeWaterSensorSensitivity(
      Cookies.get("session_key"),
      { name: waterSensor.name, sensPct: Number(sensitivity) },
      sensorId
    );

    setWaterSensor({ ...waterSensor, sensPct: Number(sensitivity) });
    onSensorUpdated?.();
  };

  const updateZoneBinding = async () => {
    if (selectedZoneId === associatedZoneId) return;

    try {
      await updateWsZoneBinding(Cookies.get("session_key"), sensorId, {
        zone_id: selectedZoneId,
      });
      setAssociatedZoneId(selectedZoneId);
      onSensorUpdated?.();
    } catch (err) {
      console.error("Failed to update zone binding:", err);
    }
  };

  if (!waterSensor) return null;

  const currentZone = zones.find((z) => z.id === associatedZoneId);
  const batteryClass =
    waterSensor.vbatt < 3.5 ? "low" : waterSensor.vbatt < 3.8 ? "medium" : "good";

  return (
    <Popup
      isVisible={isVisible}
      handleClose={onClose}
      saveButtonText={null}
      handleSave={null}
      isSaveDisabled={false}
      headerText={
        <span className="ws-header-title">
          Water Sensor {waterSensor.local_id}
          {isRealName(waterSensor.name) && (
            <span className="ws-header-subtitle"> · {waterSensor.name}</span>
          )}
        </span>
      }
      popupContent={
        <div className="ws-inspector">

          {/* Status strip */}
          <div className="ws-status-strip">
            <span className={`ws-water-badge ${waterSensor.water ? "wet" : "dry"}`}>
              {waterSensor.water ? "● Water Detected" : "● Dry"}
            </span>
            <div className={`ws-battery-pill ${batteryClass}`}>
              {waterSensor.vbatt?.toFixed(2)}V
            </div>
            {waterSensor.charging && (
              <span className="charging-flag">Charging</span>
            )}
          </div>

          {/* Editable rows */}
          <div className="ws-rows">

            {/* Name */}
            {!isEditingName ? (
              <div className="ws-row">
                <span className="ws-row-label">Name</span>
                <span className="ws-row-value">
                  {isRealName(waterSensor.name)
                    ? waterSensor.name
                    : <em className="ws-unset">Not set</em>}
                </span>
                <button className="ws-edit-btn" onClick={() => setIsEditingName(true)}>
                  Edit
                </button>
              </div>
            ) : (
              <div className="ws-row ws-row--editing">
                <span className="ws-row-label">Name</span>
                <input
                  type="text"
                  value={sensorName}
                  onChange={(e) => setSensorName(e.target.value)}
                />
                <button
                  onClick={async () => { await updateName(); setIsEditingName(false); }}
                  disabled={sensorName.trim() === (waterSensor.name || "")}
                >
                  Update
                </button>
                <button
                  className="secondary"
                  onClick={() => {
                    setSensorName(isRealName(waterSensor.name) ? waterSensor.name : "");
                    setIsEditingName(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Sensitivity */}
            {!isEditingSensitivity ? (
              <div className="ws-row">
                <span className="ws-row-label">Sensitivity</span>
                <span className="ws-row-value">{waterSensor.sensPct}%</span>
                <button className="ws-edit-btn" onClick={() => setIsEditingSensitivity(true)}>
                  Edit
                </button>
              </div>
            ) : (
              <div className="ws-row ws-row--editing">
                <span className="ws-row-label">Sensitivity</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={sensitivity}
                  onChange={(e) => setSensitivity(e.target.value)}
                />
                <button
                  onClick={async () => { await updateSensitivity(); setIsEditingSensitivity(false); }}
                  disabled={Number(sensitivity) === waterSensor.sensPct}
                >
                  Update
                </button>
                <button
                  className="secondary"
                  onClick={() => { setSensitivity(waterSensor.sensPct); setIsEditingSensitivity(false); }}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Zone */}
            {!isEditingZone ? (
              <div className="ws-row">
                <span className="ws-row-label">Zone</span>
                <span className="ws-row-value">
                  {currentZone ? currentZone.name : <em className="ws-unset">Unassigned</em>}
                </span>
                <button className="ws-edit-btn" onClick={() => setIsEditingZone(true)}>
                  Edit
                </button>
              </div>
            ) : (
              <div className="ws-row ws-row--editing">
                <span className="ws-row-label">Zone</span>
                <select
                  value={selectedZoneId ?? ""}
                  onChange={(e) =>
                    setSelectedZoneId(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">Unassigned</option>
                  {zones.map((zone) => (
                    <option key={zone.id} value={zone.id}>{zone.name}</option>
                  ))}
                </select>
                <button
                  onClick={async () => { await updateZoneBinding(); setIsEditingZone(false); }}
                  disabled={selectedZoneId === associatedZoneId}
                >
                  Update
                </button>
                <button
                  className="secondary"
                  onClick={() => { setSelectedZoneId(associatedZoneId); setIsEditingZone(false); }}
                >
                  Cancel
                </button>
              </div>
            )}

          </div>

          {/* Meta */}
          <div className="ws-meta-strip">
            <span>Serial: {waterSensor.serial}</span>
          </div>

        </div>
      }
    />
  );
};

export default WaterSensorPopup;
